import { useRef, useState } from 'react';
import type { JSX } from 'react';

import { addDays } from '@asas/domain/calendar';
import { FACHLICHE_ZEITZONE, calendarDateIn, isoWeekOf } from '@asas/domain/iso-week';
import type {
  EmployeeDetails,
  EmployeeList,
  EmployeeOptions,
  EmployeeUpdateInput,
  WeekCapacity,
} from '@asas/domain';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc } from '../trpc';

import {
  aenderungEingabe,
  anlegenEingabe,
  aufNeuenStand,
  entfernenEingabe,
  fachgruppenwechselEntfernenEingabe,
  feldMeldungen,
  formularAus,
  formularZumVervollstaendigen,
  gesellschaftswechselEntfernenEingabe,
  hatEigeneEingaben,
  istZuVervollstaendigen,
  konfliktHinweise,
  leeresFormular,
  modusGewechselt,
  pruefeFormularStand,
  stammsaetzeAusPacs,
  vervollstaendigenEingabe,
} from './formular';
import type { FormularFeld, FormularStand } from './formular';
import {
  FachgruppenHistorie,
  GesellschaftsHistorie,
  KapazitaetsVorschau,
  Kopfzeile,
  MitarbeiterFormular,
  MitarbeiterListe,
  PacsZusammenfuehren,
  SollHistorie,
} from './MitarbeiterAnsicht';

/** Wie viele Wochen die Vorschau zeigt: zwei zurueck, zehn voraus — innerhalb von ADR-07. */
const VORSCHAU_WOCHEN = 12;
const VORSCHAU_RUECKBLICK_TAGE = 14;

/**
 * Heute in der fachlichen Zeitzone, nach der Uhr des Browsers — NUR fuer die
 * Anzeige; was aenderbar ist, sagt der Server (`changesFrom`).
 */
function heuteFachlich(): string {
  return calendarDateIn(new Date(), FACHLICHE_ZEITZONE);
}

/**
 * Der Ausgangsstand des Formulars fuer einen gespeicherten Satz: bearbeiten —
 * oder, wer nicht planbar ist (Stammsatz aus PACS, extern), vervollstaendigen
 * (AC-103, Ergaenzung).
 */
function basisFuer(employee: EmployeeDetails, changesFrom: string): FormularStand {
  return istZuVervollstaendigen(employee)
    ? formularZumVervollstaendigen(employee)
    : formularAus(employee, changesFrom);
}

/**
 * Ein Entwurf haelt die Eingaben UND den Stand, von dem aus sie getippt wurden.
 * Erst beides zusammen sagt, welche Felder die Person geaendert hat
 * (`aufNeuenStand`) — der gespeicherte Stand kann sich bis zur Rueckkehr
 * geaendert haben.
 */
interface FormularEntwurf {
  readonly stand: FormularStand;
  readonly basis: FormularStand;
}

/** Prueft einen Entwurf aus dem Browserspeicher — veraenderbarer Text, kein Vertrauen (AC-016, Entscheidung 6). */
function pruefeEntwurf(roh: unknown): FormularEntwurf | null {
  if (typeof roh !== 'object' || roh === null) return null;

  const { stand, basis } = roh as Record<string, unknown>;
  const geprueft = { stand: pruefeFormularStand(stand), basis: pruefeFormularStand(basis) };

  return geprueft.stand === null || geprueft.basis === null
    ? null
    : { stand: geprueft.stand, basis: geprueft.basis };
}

export interface MitarbeiterBereichProps {
  /** Die Kontokennung der angemeldeten Person — Inhaber eines Entwurfs (AC-016, Entscheidung 7). */
  readonly inhaber: string;
}

interface Geladen {
  readonly liste: EmployeeList;
  readonly optionen: EmployeeOptions;
}

type Auswahl = { readonly modus: 'neu' } | { readonly modus: 'bearbeiten'; readonly id: string };

/** Warum Speichern gesperrt ist, obwohl gerade nichts laeuft — bis zum naechsten erfolgreichen Laden. */
type Sperre =
  /** 403: jeder weitere Versuch schriebe eine unloeschbare Audit-Zeile (AC-007). */
  | 'zugriff'
  /** Anlegen mit ungewissem Ausgang: ein zweiter Klick legte die Person womoeglich doppelt an. */
  | 'unklar';

/**
 * Eine Meldung ueber dem Formular. Ein Fehler steht in `role="alert"`, ein
 * Hinweis in der dauerhaft vorhandenen `role="status"`-Region — eine Region,
 * die erst mit ihrem Text entsteht, liest ein Bildschirmleser nicht sicher vor.
 */
interface Meldung {
  readonly text: string;
  readonly art: 'hinweis' | 'fehler';
}

/** Wohin nach der Neuanmeldung? Mit oder ohne gesicherte Eingaben. */
type Anmeldung = 'keine' | 'mitEntwurf' | 'ohneEntwurf';

/**
 * Die Mitarbeiterverwaltung der Systemadministration (AC-103): anzeigen,
 * anlegen, bearbeiten, Soll-Historie, Kapazitaetsvorschau.
 *
 * Nach dem Rezept aus AC-016 (README, „Eine neue Mutation anschliessen"):
 * Schreibzugriffe laufen ueber die GETEILTE Warteschlange je Mitarbeiter, jeder
 * Fehler geht durch `klassifiziereFehler`, und nach Erfolg gilt die
 * SERVERANTWORT, nicht die eigene Annahme.
 *
 * Das Formular merkt sich neben den Eingaben den Stand, von dem aus sie getippt
 * wurden (`basis`). Kommt ein neuer Stand an — „Neu laden" nach einem Konflikt,
 * ein Entwurf nach der Neuanmeldung —, bleiben nur die Felder der Person, alle
 * anderen zeigen den neuen Stand (`aufNeuenStand`). Sonst schickte das
 * Formular die alten Werte unberuehrter Felder mit und drehte die Aenderung
 * einer anderen Person still zurueck.
 *
 * Hat dabei auch die andere Seite ein Feld geaendert, das die Person
 * geaendert hat, bleibt ihre Eingabe stehen — mit einem Hinweis am Feld, was
 * inzwischen gespeichert ist (`konfliktHinweise`).
 *
 * WAEHREND GELADEN WIRD, IST DIE MASKE GESPERRT: Auswahl, Anlegen, Entfernen,
 * Eingaben, Speichern und Abbrechen. Die Antwort wird auf den Stand bezogen,
 * der beim Klick offen war; wechselte die Auswahl dazwischen, landeten die
 * Werte des einen Mitarbeiters im Formular des anderen. Zusaetzlich wird nur
 * umbasiert, wenn noch dieselbe Auswahl offen ist (`auswahlJetzt`).
 *
 * GELADEN WIRD ERST AUF ANFORDERUNG, nicht beim Seitenaufbau. Ohne
 * `employee:manage` antwortet der Server mit 403, und jede Ablehnung schreibt
 * eine unloeschbare Audit-Zeile (AC-007). Ein automatisches Laden bei jedem
 * Seitenaufruf jeder Person waere genau die Wiederholungsschleife, vor der
 * AC-016 (Entscheidung 3) warnt. Es gibt noch keinen Weg, die eigenen Rechte
 * abzufragen; bis dahin entscheidet die Person mit einem Klick. Deshalb gibt
 * es in dieser Datei auch keinen Effekt (`MitarbeiterAnsicht.spec.tsx` haelt
 * das fest).
 */
export function MitarbeiterBereich({ inhaber }: MitarbeiterBereichProps): JSX.Element {
  const [geladen, setGeladen] = useState<Geladen | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [ladefehler, setLadefehler] = useState<FehlerBefund | null>(null);
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const [stand, setStand] = useState<FormularStand>(leeresFormular);
  const [basis, setBasis] = useState<FormularStand>(leeresFormular);
  const [fehler, setFehler] = useState<Partial<Record<FormularFeld, string>>>({});
  const [feldHinweise, setFeldHinweise] = useState<Partial<Record<FormularFeld, string>>>({});
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const [anmeldung, setAnmeldung] = useState<Anmeldung>('keine');
  const [sperre, setSperre] = useState<Sperre | null>(null);
  const [sendet, setSendet] = useState(false);
  const [wochen, setWochen] = useState<readonly WeekCapacity[] | null>(null);
  const [vorschauMeldung, setVorschauMeldung] = useState<string | null>(null);
  // Zusammenfuehren mit einem Stammsatz aus PACS (AC-103, Ergaenzung): welcher
  // Satz gewaehlt ist und was der Server dazu am Feld gemeldet hat.
  const [zusammenfuehrenMit, setZusammenfuehrenMit] = useState('');
  const [zusammenfuehrenFehler, setZusammenfuehrenFehler] = useState<string | undefined>();
  // Fuer wen die Vorschau gerade laedt. Kommt eine Antwort fuer jemand anderen
  // an (Auswahl inzwischen gewechselt), wird sie verworfen.
  const vorschauFuer = useRef<string | null>(null);
  // Die Auswahl, die JETZT offen ist — fuer Antworten, die nach einem Warten
  // ankommen. Gesetzt nur ueber `waehle`.
  const auswahlJetzt = useRef<Auswahl | null>(null);

  const entwuerfe = erzeugeEntwurfsspeicher({ inhaber });
  const heute = heuteFachlich();
  // `window` fehlt nur beim Rendern ausserhalb des Browsers (Tests).
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  function entwurfsSchluessel(ziel: Auswahl): string {
    return entitaetsSchluessel('mitarbeiter-formular', ziel.modus === 'neu' ? 'neu' : ziel.id);
  }

  function waehle(ziel: Auswahl | null): void {
    auswahlJetzt.current = ziel;
    setAuswahl(ziel);
  }

  function hinweis(text: string): void {
    setMeldung({ text, art: 'hinweis' });
  }

  function zuruecksetzen(): void {
    setZusammenfuehrenFehler(undefined);
    setFehler({});
    setFeldHinweise({});
    setMeldung(null);
    setAnmeldung('keine');
  }

  function vorschauSchliessen(): void {
    vorschauFuer.current = null;
    setWochen(null);
    setVorschauMeldung(null);
  }

  async function laden(): Promise<void> {
    // Was beim Klick offen war. Solange geladen wird, ist die Maske gesperrt;
    // `stand` und `basis` dieses Aufrufs sind deshalb auch nach dem Warten die
    // aktuellen.
    const offen = auswahl;

    setLaedt(true);
    setLadefehler(null);

    try {
      // Beide Abfragen in EINEM Stapel (httpBatchLink, AC-016).
      const [liste, optionen] = await Promise.all([
        trpc.employees.list.query(),
        trpc.employees.options.query(),
      ]);
      setGeladen({ liste, optionen });
      setSperre(null);

      if (auswahlJetzt.current !== offen) return;

      if (sperre === 'unklar' && offen?.modus === 'neu') {
        hinweis(
          'Neu geladen. Bitte prüfen Sie in der Liste, ob der Mitarbeiter bereits angelegt wurde, bevor Sie erneut speichern.',
        );
      }

      if (offen?.modus === 'bearbeiten') {
        const neu = liste.employees.find((employee) => employee.id === offen.id);

        if (neu === undefined) {
          waehle(null);
          vorschauSchliessen();
        } else {
          const neueBasis = basisFuer(neu, liste.changesFrom);
          const konflikte = konfliktHinweise(stand, basis, neueBasis, optionen);

          setStand(aufNeuenStand(stand, basis, neueBasis));
          setBasis(neueBasis);
          setFeldHinweise(konflikte);
          hinweis(
            modusGewechselt(basis, neueBasis) && hatEigeneEingaben(stand, basis)
              ? 'Neu geladen. Der Stammsatz wurde inzwischen vervollständigt oder geändert; Ihre Eingaben wurden verworfen. Bitte prüfen Sie den gespeicherten Stand.'
              : 'Neu geladen. Ihre ungespeicherten Eingaben sind erhalten; alle anderen Felder zeigen den gespeicherten Stand.' +
                  (Object.keys(konflikte).length > 0
                    ? ' Bei markierten Feldern ist inzwischen ein anderer Wert gespeichert.'
                    : ''),
          );
          void kapazitaetLaden(neu);
        }
      }
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      setLadefehler(befund);

      // Die Anmeldung ist ein Seitenwechsel: offene Eingaben gehen mit, wie
      // beim Speichern (`fehlerZeigen`) — auch solche, die erst nach dieser
      // Meldung getippt werden: Der Link unter der Liste sichert beim Klick
      // erneut. Ohne eigene Eingaben merkt `entwurfSichern` nichts.
      if (befund.neuAnmelden && offen !== null) {
        setAnmeldung(entwurfSichern(offen) ? 'mitEntwurf' : 'ohneEntwurf');
      }
    } finally {
      setLaedt(false);
    }
  }

  /**
   * Merkt die Eingaben fuer die Rueckkehr nach der Anmeldung; `false`, wenn der
   * Browser es nicht kann. Ohne eigene Eingaben wird nichts gemerkt — und ein
   * aelterer Entwurf vergessen: sonst meldete das Formular nach der Anmeldung
   * „wiederhergestellt", obwohl niemand etwas getippt hat.
   */
  function entwurfSichern(ziel: Auswahl): boolean {
    if (!hatEigeneEingaben(stand, basis)) {
      entwuerfe.vergessen(entwurfsSchluessel(ziel));
      return true;
    }

    const entwurf: FormularEntwurf = { stand, basis };

    return entwuerfe.merken(entwurfsSchluessel(ziel), entwurf);
  }

  async function kapazitaetLaden(employee: EmployeeDetails): Promise<void> {
    vorschauFuer.current = employee.id;
    setWochen(null);
    setVorschauMeldung(null);

    try {
      const antwort = await trpc.employees.capacity.query({
        employeeId: employee.id,
        from: isoWeekOf(addDays(heute, -VORSCHAU_RUECKBLICK_TAGE)),
        weeks: VORSCHAU_WOCHEN,
      });

      if (vorschauFuer.current === antwort.employeeId) setWochen(antwort.weeks);
    } catch (error) {
      if (vorschauFuer.current !== employee.id) return;

      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      // Eine eigene Meldung: sie darf ein „Gespeichert." nicht verdraengen und
      // zu einem zweiten Schreibversuch einladen.
      if (befund.art !== 'abgebrochen') {
        setVorschauMeldung(`Die Kapazitätsvorschau ist nicht verfügbar: ${befund.meldung}`);
      }
    }
  }

  /**
   * Oeffnet den Editor; ein Entwurf von vor der Neuanmeldung wird angeboten, nie
   * abgeschickt (AC-016, Entscheidung 9). Eine Sperre bleibt bis zum naechsten
   * erfolgreichen Laden — auch ueber das Oeffnen eines anderen Formulars hinweg.
   */
  function oeffnen(ziel: Auswahl, ausgangsstand: FormularStand, optionen: EmployeeOptions): void {
    zuruecksetzen();
    waehle(ziel);
    setBasis(ausgangsstand);

    const entwurf = entwuerfe.holen(entwurfsSchluessel(ziel), pruefeEntwurf);
    // Sonst stuende ein gesperrtes „Speichern" ohne Grund da.
    const gesperrt =
      sperre === null ? '' : ' Speichern bleibt gesperrt, bis Sie die Ansicht neu geladen haben.';

    if (entwurf === null) {
      setStand(ausgangsstand);
      if (gesperrt !== '') hinweis(gesperrt.trim());
    } else {
      const { stand: eingabe, basis: vorher } = entwurf.eingabe;
      const konflikte = konfliktHinweise(eingabe, vorher, ausgangsstand, optionen);

      setStand(aufNeuenStand(eingabe, vorher, ausgangsstand));
      setFeldHinweise(konflikte);
      hinweis(
        (modusGewechselt(vorher, ausgangsstand)
          ? 'Ihre Eingaben von vor der Anmeldung passen nicht mehr: der Stammsatz wurde inzwischen vervollständigt oder geändert. Bitte prüfen Sie den gespeicherten Stand.'
          : 'Ihre Eingaben von vor der Anmeldung wurden wiederhergestellt. Bitte prüfen und speichern.') +
          (Object.keys(konflikte).length > 0
            ? ' Bei markierten Feldern ist inzwischen ein anderer Wert gespeichert.'
            : '') +
          gesperrt,
      );
    }
  }

  function bearbeiten(id: string): void {
    const employee = geladen?.liste.employees.find((kandidat) => kandidat.id === id);

    if (employee === undefined || geladen === null) return;

    setZusammenfuehrenMit('');
    oeffnen(
      { modus: 'bearbeiten', id },
      basisFuer(employee, geladen.liste.changesFrom),
      geladen.optionen,
    );
    void kapazitaetLaden(employee);
  }

  /** Ersetzt den Mitarbeiter im Zustand durch die Serverantwort (AC-016, AK-2). */
  function uebernehmen(employee: EmployeeDetails): void {
    setGeladen((vorher) => {
      if (vorher === null) return vorher;

      const bekannt = vorher.liste.employees.some((kandidat) => kandidat.id === employee.id);
      const employees = bekannt
        ? vorher.liste.employees.map((kandidat) =>
            kandidat.id === employee.id ? employee : kandidat,
          )
        : [...vorher.liste.employees, employee];

      return { ...vorher, liste: { ...vorher.liste, employees } };
    });
  }

  /**
   * Zeigt einen Fehler eines Schreibzugriffs. `feldbezogen: false`, wenn die
   * Felder des Formulars den Vorgang nicht beschreiben (Entfernen aus der
   * Historie): dann gehen auch Feldbefunde in die Meldung, nicht an ein Feld.
   */
  function fehlerZeigen(error: unknown, ziel: Auswahl, feldbezogen: boolean): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: true });

    if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;

    if (befund.neuAnmelden) {
      // Die Anmeldung ist ein Seitenwechsel; der Entwurf ueberlebt ihn — wenn
      // der Browser ihn speichern kann. Sonst wird der Weg trotzdem angeboten.
      setAnmeldung(entwurfSichern(ziel) ? 'mitEntwurf' : 'ohneEntwurf');
    }

    if (befund.art === 'zugriff') {
      setSperre('zugriff');
    } else if (ziel.modus === 'neu' && befund.moeglicherweiseAusgefuehrt) {
      setSperre('unklar');
    }

    const { jeFeld, allgemein } = feldMeldungen(befund.felder);

    setFehler(feldbezogen ? jeFeld : {});
    setMeldung({
      art: 'fehler',
      text: befund.moeglicherweiseAusgefuehrt
        ? `${befund.meldung} Der Vorgang könnte bereits gespeichert sein – bitte laden Sie die Ansicht neu und prüfen Sie den Stand, bevor Sie es erneut versuchen.`
        : [
            befund.meldung,
            ...(feldbezogen ? allgemein : befund.felder.map((feld) => feld.message)),
          ].join(' '),
    });
  }

  /**
   * Ein Schreibzugriff. `eingabenBehalten`: Tippt jemand im Formular und
   * entfernt dann einen Eintrag der Historie, bleiben seine Eingaben stehen —
   * nur die Felder, die er nicht angefasst hat, zeigen den neuen Stand.
   */
  async function schreiben(
    ziel: Auswahl,
    schluessel: string,
    aufruf: () => Promise<EmployeeDetails>,
    { eingabenBehalten }: { readonly eingabenBehalten: boolean },
  ): Promise<void> {
    zuruecksetzen();
    setSendet(true);

    try {
      const employee = await geteilterSerialisierer.reihe(schluessel, aufruf);

      uebernehmen(employee);
      entwuerfe.vergessen(entwurfsSchluessel(ziel));

      if (geladen !== null) {
        const neueBasis = basisFuer(employee, geladen.liste.changesFrom);

        waehle({ modus: 'bearbeiten', id: employee.id });
        setStand((vorher) =>
          eingabenBehalten ? aufNeuenStand(vorher, basis, neueBasis) : neueBasis,
        );
        setBasis(neueBasis);
      }

      hinweis('Gespeichert.');
      void kapazitaetLaden(employee);
    } catch (error) {
      fehlerZeigen(error, ziel, !eingabenBehalten);
    } finally {
      setSendet(false);
    }
  }

  function speichern(): void {
    if (auswahl === null || sperre !== null || laedt) return;

    if (auswahl.modus === 'neu') {
      const eingabe = anlegenEingabe(stand);

      void schreiben(
        auswahl,
        entitaetsSchluessel('employees', 'neu'),
        () => trpc.employees.create.mutate(eingabe),
        { eingabenBehalten: false },
      );
      return;
    }

    const employee = geladen?.liste.employees.find((kandidat) => kandidat.id === auswahl.id);

    if (employee === undefined) return;

    // AC-103, Ergaenzung: wer nicht planbar ist, wird vervollstaendigt.
    if (istZuVervollstaendigen(employee)) {
      const eingabe = vervollstaendigenEingabe(employee, stand);

      void schreiben(
        auswahl,
        entitaetsSchluessel('employees', employee.id),
        () => trpc.employees.complete.mutate(eingabe),
        { eingabenBehalten: false },
      );
      return;
    }

    const eingabe = aenderungEingabe(employee, stand);

    if (eingabe === null) {
      zuruecksetzen();
      hinweis('Keine Änderung zu speichern.');
      return;
    }

    aendern(auswahl, eingabe, false);
  }

  function aendern(ziel: Auswahl, eingabe: EmployeeUpdateInput, eingabenBehalten: boolean): void {
    void schreiben(
      ziel,
      entitaetsSchluessel('employees', eingabe.id),
      () => trpc.employees.update.mutate(eingabe),
      { eingabenBehalten },
    );
  }

  /**
   * Fuehrt den gewaehlten Satz mit einem Stammsatz aus PACS zusammen (AC-103,
   * Ergaenzung). Der Server entfernt den Stammsatz aus PACS; die Liste folgt
   * der Antwort: der Satz kommt aus der Antwort, der entfernte faellt weg.
   */
  async function zusammenfuehren(ziel: EmployeeDetails, stammsatzId: string): Promise<void> {
    const stammsatz = geladen?.liste.employees.find((kandidat) => kandidat.id === stammsatzId);

    if (stammsatz === undefined || stammsatz.pacsId === null || sperre !== null || laedt) return;

    const pacsId = stammsatz.pacsId;
    const auswahlZiel: Auswahl = { modus: 'bearbeiten', id: ziel.id };

    zuruecksetzen();
    setSendet(true);

    try {
      const employee = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('employees', ziel.id),
        () =>
          trpc.employees.mergePacs.mutate({
            id: ziel.id,
            expectedVersion: ziel.version,
            pacsId,
          }),
      );

      uebernehmen(employee);
      setGeladen((vorher) =>
        vorher === null
          ? vorher
          : {
              ...vorher,
              liste: {
                ...vorher.liste,
                employees: vorher.liste.employees.filter(
                  (kandidat) => kandidat.id !== stammsatzId || kandidat.id === employee.id,
                ),
              },
            },
      );
      setZusammenfuehrenMit('');

      if (geladen !== null) {
        const neueBasis = basisFuer(employee, geladen.liste.changesFrom);

        // Der Name kommt ab jetzt aus PACS; eine vorher getippte Eingabe im nun
        // gesperrten Feld zeigte sonst einen Namen, der nie gespeichert wird.
        setStand((vorher) => ({
          ...aufNeuenStand(vorher, basis, neueBasis),
          name: neueBasis.name,
        }));
        setBasis(neueBasis);
      }

      hinweis('Zusammengeführt. Der Name kommt jetzt aus PACS.');
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: true });
      const amFeld = befund.felder.find((feld) => feld.path === 'pacsId');

      setZusammenfuehrenFehler(amFeld?.message);
      fehlerZeigen(error, auswahlZiel, false);
    } finally {
      setSendet(false);
    }
  }

  const kopfzeile = (
    <Kopfzeile
      geladen={geladen !== null}
      laedt={laedt}
      sendet={sendet}
      ladefehler={ladefehler}
      anmeldeUrl={anmeldeUrl}
      anmeldungAngeboten={anmeldung !== 'keine'}
      onLaden={() => {
        void laden();
      }}
      onNeu={() => {
        if (geladen === null) return;

        oeffnen({ modus: 'neu' }, leeresFormular(), geladen.optionen);
        vorschauSchliessen();
      }}
    />
  );

  if (geladen === null) {
    return (
      <section>
        <h2>Mitarbeiter-Stammdaten</h2>
        {kopfzeile}
      </section>
    );
  }

  const { liste, optionen } = geladen;
  const ausgewaehlt =
    auswahl?.modus === 'bearbeiten'
      ? liste.employees.find((employee) => employee.id === auswahl.id)
      : undefined;
  const vervollstaendigen = ausgewaehlt !== undefined && istZuVervollstaendigen(ausgewaehlt);

  return (
    <section>
      <h2>Mitarbeiter-Stammdaten</h2>
      {kopfzeile}

      <MitarbeiterListe
        mitarbeiter={liste.employees}
        optionen={optionen}
        heute={heute}
        ausgewaehlt={auswahl?.modus === 'bearbeiten' ? auswahl.id : null}
        gesperrt={sendet || laedt}
        onAuswahl={bearbeiten}
      />

      <p role="status">{meldung?.art === 'hinweis' ? meldung.text : null}</p>
      {meldung?.art === 'fehler' ? <p role="alert">{meldung.text}</p> : null}
      {anmeldung === 'keine' ? null : (
        <p>
          <a
            href={anmeldeUrl}
            onClick={() => {
              // Was seit der Meldung noch getippt wurde, geht mit.
              if (auswahl !== null && anmeldung === 'mitEntwurf') entwurfSichern(auswahl);
            }}
          >
            {anmeldung === 'ohneEntwurf'
              ? 'Neu anmelden – Ihre Eingaben konnten nicht gesichert werden; bitte vorher notieren'
              : hatEigeneEingaben(stand, basis)
                ? 'Neu anmelden – Ihre Eingaben bleiben erhalten'
                : 'Neu anmelden'}
          </a>
        </p>
      )}

      {auswahl === null ? null : (
        <MitarbeiterFormular
          modus={vervollstaendigen ? 'vervollstaendigen' : auswahl.modus}
          stand={stand}
          optionen={optionen}
          changesFrom={liste.changesFrom}
          {...(ausgewaehlt === undefined
            ? {}
            : {
                aktuelleFachgruppeId: ausgewaehlt.fachgruppeId,
                aktuelleGesellschaftId: ausgewaehlt.companyId,
                nameAusPacs: ausgewaehlt.pacsId !== null,
              })}
          fehler={fehler}
          hinweise={feldHinweise}
          sendet={sendet}
          gesperrt={sendet || laedt}
          speichernGesperrt={laedt || sperre !== null}
          onAendern={(feld, wert) => {
            setStand((vorher) => ({ ...vorher, [feld]: wert }));
          }}
          onSpeichern={speichern}
          onAbbrechen={() => {
            waehle(null);
            vorschauSchliessen();
            zuruecksetzen();
          }}
        />
      )}

      {ausgewaehlt === undefined ||
      ausgewaehlt.recordStatus !== 'vollstaendig' ||
      ausgewaehlt.pacsId !== null ? null : (
        <PacsZusammenfuehren
          kandidaten={stammsaetzeAusPacs(liste.employees)}
          auswahl={zusammenfuehrenMit}
          fehler={zusammenfuehrenFehler}
          gesperrt={sendet || laedt || sperre !== null}
          onAuswahl={setZusammenfuehrenMit}
          onZusammenfuehren={() => {
            void zusammenfuehren(ausgewaehlt, zusammenfuehrenMit);
          }}
        />
      )}

      {ausgewaehlt === undefined || vervollstaendigen ? null : (
        <GesellschaftsHistorie
          eintraege={ausgewaehlt.companies}
          optionen={optionen}
          changesFrom={liste.changesFrom}
          sendet={sendet || laedt || sperre !== null}
          onEntfernen={(effectiveFrom) => {
            aendern(
              { modus: 'bearbeiten', id: ausgewaehlt.id },
              gesellschaftswechselEntfernenEingabe(ausgewaehlt, effectiveFrom),
              true,
            );
          }}
        />
      )}

      {ausgewaehlt === undefined || vervollstaendigen ? null : (
        <FachgruppenHistorie
          eintraege={ausgewaehlt.fachgruppen}
          optionen={optionen}
          changesFrom={liste.changesFrom}
          sendet={sendet || laedt || sperre !== null}
          onEntfernen={(effectiveFrom) => {
            aendern(
              { modus: 'bearbeiten', id: ausgewaehlt.id },
              fachgruppenwechselEntfernenEingabe(ausgewaehlt, effectiveFrom),
              true,
            );
          }}
        />
      )}

      {ausgewaehlt === undefined || vervollstaendigen ? null : (
        <SollHistorie
          eintraege={ausgewaehlt.weeklyHours}
          changesFrom={liste.changesFrom}
          gesperrt={sendet || laedt || sperre !== null}
          onEntfernen={(effectiveFrom) => {
            aendern(
              { modus: 'bearbeiten', id: ausgewaehlt.id },
              entfernenEingabe(ausgewaehlt, effectiveFrom),
              true,
            );
          }}
        />
      )}

      {ausgewaehlt === undefined ? null : (
        <>
          {vorschauMeldung === null ? null : <p role="alert">{vorschauMeldung}</p>}
          {wochen === null ? null : <KapazitaetsVorschau wochen={wochen} />}
        </>
      )}
    </section>
  );
}
